import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ButtonItem from '../components/ButtonItem';
import GenericAutoCompleteInput from '../components/GenericAutoCompleteInput';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquarePlus, faSquareMinus, faChevronDown, faChevronUp, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { GENRES } from '../data/genres';

function AlbumModifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const page = params.get('page');
  const search = params.get('search');
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
  const { id } = useParams();

  // 메타데이터 상태
  const [albumTypeId, setAlbumTypeId] = useState('');
  const [genreId, setGenreId] = useState('');
  const [feeRate, setFeeRate] = useState('');
  const [nameKr, setNameKr] = useState('');
  const [code, setCode] = useState('');
  const [upc, setUpc] = useState('');
  const [barcode, setBarcode] = useState('');
  const [releaseDateTime, setReleaseDateTime] = useState('');
  const [adultYn, setAdultYn] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [label, setLabel] = useState('');
  const [memo, setMemo] = useState('');


  // 이미지 상태
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [image, setImage] = useState(
    'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg'
  );

  // N:N 권리사
  const [clients, setClients] = useState([]);

  // 아티스트
  const [artist, setArtist] = useState({ id: null, name: '' });

  // 곡별 권리사 설정
  const [songs, setSongs] = useState([]);
  const [songClients, setSongClients] = useState({}); // { [songId]: [{ id, name, feeRate }] }
  const [expandedSongs, setExpandedSongs] = useState(new Set());

  // 관리자
  const user = useSelector((s) => s.auth?.me);
  const isAdmin = user?.adminYn === 'Y';

  // 별도정산
  const [commissionShares, setCommissionShares] = useState([]);
  const [newCs, setNewCs] = useState({ id: null, name: '', rate: '', memo: '' });
  const [retroLoading, setRetroLoading] = useState(false);

  const fetchCommissionShares = useCallback(async () => {
    try {
      const res = await axios.get(`/api/commission-shares?albumId=${id}`);
      setCommissionShares(res.data || []);
    } catch (e) { console.error(e); }
  }, [id]);

  // 초기 로드
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/album/${id}`
        );
        setAlbumTypeId(data.albumTypeId || '');
        setGenreId(data.genreId || '');
        setFeeRate(data.feeRate != null ? data.feeRate.toString() : '');
        setNameKr(data.nameKr || '');
        setCode(data.code || '');
        setUpc(data.upc || '');
        setBarcode(data.barcode || '');
        setReleaseDateTime(data.releaseDateTime ? data.releaseDateTime.split('T')[0] : '');
        setAdultYn(data.adultYn || '');
        setServiceType(data.serviceType || '');
        setLabel(data.label || '');
        setMemo(data.memo || '');
        setImage(data.image || 'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg');
        setImageUrlInput(data.image || '');
        setClients(
          (data.albumClients || []).map((ac) => ({
            id: ac.client?.id,
            name: ac.client?.name || '',
            feeRate: ac.feeRate,
          }))
        );
        setArtist({ id: data.artistId || null, name: data.artist?.nameKr || '' });

        // 곡 목록 + songClients 로드
        const songsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/album/${id}/songs`
        );
        setSongs(songsRes.data.items || []);
        const scMap = {};
        for (const song of (songsRes.data.items || [])) {
          if (song.songClients && song.songClients.length > 0) {
            scMap[song.id] = song.songClients.map((sc) => ({
              id: sc.client.id,
              name: sc.client.name,
              feeRate: sc.feeRate,
            }));
          }
        }
        setSongClients(scMap);
        // 이미 커스텀 설정된 곡은 펼쳐서 보여주기
        setExpandedSongs(new Set(Object.keys(scMap).map(Number)));
      } catch (e) {
        console.error('Load error:', e);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchCommissionShares();
  }, [isAdmin, fetchCommissionShares]);

  // 옵션 fetchers
  const fetchClientNames = async (search = '') => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/client/?search=${search}&limit=1000`
    );
    return res.data.items.map((c) => ({ id: Number(c.id), name: c.name }));
  };
  const fetchArtistNames = async (search = '') => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/artist/?search=${search}&limit=1000`
    );
    return res.data.items.map((a) => ({ id: Number(a.id), name: a.nameKr }));
  };

  // 권리사 추가/삭제/변경
  const addClient = () =>
    setClients([...clients, { id: -1, name: '', feeRate: '' }]);
  const removeClient = (idx) => setClients(clients.filter((_, i) => i !== idx));
  const onChangeClient = (opt, idx) => {
    if (!opt) return;
    const arr = [...clients];
    arr[idx] = { ...arr[idx], id: opt.id, name: opt.name };
    setClients(arr);
  };
  const onChangeFeeRate = (e, idx) => {
    const v = e.target.value;
    if (/^\d*$/.test(v)) {
      const arr = [...clients];
      arr[idx].feeRate = v === '' ? '' : parseInt(v, 10);
      setClients(arr);
    }
  };
  const confirmFeeRates = () => {
    const total = clients.reduce((s, c) => s + Number(c.feeRate || 0), 0);
    if (total !== 100) {
      alert(`총합은 100이어야 합니다: 현재 ${total}`);
      return false;
    }
    return true;
  };

  // 곡별 권리사 핸들러
  const toggleSongExpand = (songId) => {
    setExpandedSongs((prev) => {
      const next = new Set(prev);
      if (next.has(songId)) {
        next.delete(songId);
      } else {
        next.add(songId);
        // 펼칠 때 커스텀 설정이 없으면 앨범 기본값으로 초기화
        if (!songClients[songId]) {
          setSongClients((prev) => ({
            ...prev,
            [songId]: clients.map((c) => ({ ...c })),
          }));
        }
      }
      return next;
    });
  };
  const resetSongClient = (songId) => {
    setSongClients((prev) => {
      const next = { ...prev };
      delete next[songId];
      return next;
    });
    setExpandedSongs((prev) => {
      const next = new Set(prev);
      next.delete(songId);
      return next;
    });
  };
  const onChangeSongClient = (opt, songId, idx) => {
    if (!opt) return;
    setSongClients((prev) => {
      const arr = [...(prev[songId] || [])];
      arr[idx] = { ...arr[idx], id: opt.id, name: opt.name };
      return { ...prev, [songId]: arr };
    });
  };
  const onChangeSongFeeRate = (e, songId, idx) => {
    const v = e.target.value;
    if (/^\d*$/.test(v)) {
      setSongClients((prev) => {
        const arr = [...(prev[songId] || [])];
        arr[idx] = { ...arr[idx], feeRate: v === '' ? '' : parseInt(v, 10) };
        return { ...prev, [songId]: arr };
      });
    }
  };
  const addSongClient = (songId) => {
    setSongClients((prev) => ({
      ...prev,
      [songId]: [...(prev[songId] || []), { id: -1, name: '', feeRate: '' }],
    }));
  };
  const removeSongClient = (songId, idx) => {
    setSongClients((prev) => ({
      ...prev,
      [songId]: (prev[songId] || []).filter((_, i) => i !== idx),
    }));
  };

  // 이미지 change
  const imageValueChange = async (e) => {
    const url = e.target.value;
    setImageUrlInput(url);
    if (!url)
      return setImage(
        'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg'
      );
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/album/imageDownload`,
        { url }
      );
      setImage(res.data.imagePath);
    } catch {
      setImage(
        'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg'
      );
    }
  };

  // 저장
  const modify = async () => {
    if (
      !clients.length ||
      !artist.id ||
      !albumTypeId ||
      !genreId ||
      !feeRate ||
      !nameKr ||
      !code ||
      !upc ||
      !releaseDateTime ||
      !adultYn ||
      !serviceType ||
      !label
    ) {
      return alert('필수 입력해주세요.');
    }
    if (!confirmFeeRates()) return;

    // 곡별 권리사 배분율 검증
    for (const [songId, scList] of Object.entries(songClients)) {
      const total = scList.reduce((s, c) => s + Number(c.feeRate || 0), 0);
      if (total !== 100) {
        const songName = songs.find((s) => s.id === Number(songId))?.nameKr || songId;
        alert(`곡 "${songName}"의 배분율 총합이 100이 아닙니다: 현재 ${total}`);
        return;
      }
      if (scList.some((c) => !c.id || c.id === -1)) {
        const songName = songs.find((s) => s.id === Number(songId))?.nameKr || songId;
        alert(`곡 "${songName}"에 권리사가 선택되지 않은 항목이 있습니다.`);
        return;
      }
    }

    const payload = {
      clients,
      artistId: artist.id,
      albumTypeId,
      genreId,
      feeRate: Number(feeRate),
      nameKr,
      code,
      upc,
      barcode,
      releaseDateTime,
      adultYn,
      serviceType,
      label,
      memo,
      image,
      songClients: Object.entries(songClients).map(([songId, scClients]) => ({
        songId: Number(songId),
        clients: scClients,
      })),
    };
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/album/${id}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      alert('수정 완료');
      navigate(`/Albums/${id}?page=${page}${searchParam}`);
    } catch (e) {
      alert('오류');
    }
  };

  if (!clients.length)
    return (
      <div className="flex-1 p-8 text-center">로딩중...</div>
    );

  return (
    <>
        <h1 className="text-xl sm:text-2xl font-bold text-center mb-8">
          앨범 수정
        </h1>
        <div className="mx-auto max-w-2xl space-y-6">
          {/* 권리사 */}
          <div>
            <label className="block mb-1">
              <span className="font-semibold">권리사명 </span>
              <span className="text-sm text-slate-400">
                (분배율의 총합은 100이어야 함)
              </span>
            </label>
            {clients.map((c, i) => (
              <div key={i} className="flex items-center gap-4 mb-3">
                <GenericAutoCompleteInput
                  value={c}
                  onChange={onChangeClient}
                  fetchOptions={fetchClientNames}
                  placeholder="권리사 선택"
                  index={i}
                  clients={clients}
                />
                <input
                  className="w-24 px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200"
                  placeholder="%"
                  value={c.feeRate || ''}
                  onChange={(e) => onChangeFeeRate(e, i)}
                />
                <FontAwesomeIcon
                  icon={faSquarePlus}
                  className={`text-2xl ${
                    i === clients.length - 1
                      ? 'text-sky-500 cursor-pointer'
                      : 'text-gray-300'
                  }`}
                  onClick={i === clients.length - 1 ? addClient : undefined}
                />
                <FontAwesomeIcon
                  icon={faSquareMinus}
                  className="text-2xl text-red-400 cursor-pointer"
                  onClick={() => removeClient(i)}
                />
              </div>
            ))}
          </div>

          {/* 아티스트 */}
          <div>
            <label className="block font-semibold mb-1">아티스트명</label>
            <GenericAutoCompleteInput
              value={artist}
              onChange={(opt) =>
                opt && setArtist({ id: opt.id, name: opt.name })
              }
              fetchOptions={fetchArtistNames}
              placeholder="아티스트 선택"
            />
          </div>

          {/* 이미지 */}
          <div>
            <label className="block font-semibold mb-1">이미지 URL</label>
            <input
              type="text"
              value={imageUrlInput}
              onChange={imageValueChange}
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200"
              placeholder="URL 입력"
            />
            <img
              src={
                image ||
                'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg'
              }
              alt={nameKr}
              onError={(e) => {
                e.target.src =
                  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';
              }}
              className="w-40 h-40 rounded-full object-cover mx-auto mt-2 border border-white/[0.08]"
            />
          </div>

          {/* 메타데이터 입력 */}
          {[
            {
              label: '앨범명',
              value: nameKr,
              onChange: (e) => setNameKr(e.target.value),
            },
            {
              label: '코드',
              value: code,
              onChange: (e) => setCode(e.target.value),
            },
            {
              label: 'UPC',
              value: upc,
              onChange: (e) => setUpc(e.target.value),
            },
            {
              label: '바코드',
              value: barcode,
              onChange: (e) => setBarcode(e.target.value),
            },
            {
              label: '발매일',
              value: releaseDateTime,
              type: 'date',
              onChange: (e) => setReleaseDateTime(e.target.value),
            },
            {
              label: '레이블',
              value: label,
              onChange: (e) => setLabel(e.target.value),
            },
            {
              label: '기본수수료',
              value: feeRate,
              onChange: (e) => setFeeRate(e.target.value),
            },
          ].map((it, k) => (
            <div key={k}>
              <label className="block font-semibold mb-1">{it.label}</label>
              <input
                type={it.type || 'text'}
                value={it.value}
                onChange={it.onChange}
                className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200"
              />
            </div>
          ))}

          {/* 장르 */}
          <div>
            <label className="block font-semibold mb-1">장르</label>
            <select
              value={genreId}
              onChange={(e) =>
                setGenreId(
                  e.target.value === '' ? null : Number(e.target.value)
                )
              }
              className="w-full px-2 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent">
              <option value="">장르 선택</option>
              {GENRES.map((g) => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
          </div>

          {/* 앨범타입 */}
          <div>
            <label className="block font-semibold mb-1">앨범타입</label>
            <div className="flex gap-6">
              {['싱글', 'EP', '정규', '컴필레이션'].map((t, i) => (
                <label key={i} className="flex items-center gap-1">
                  <input
                    type="radio"
                    checked={albumTypeId === i + 1}
                    onChange={() => setAlbumTypeId(i + 1)}
                  />
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 19금 */}
          <div>
            <label className="block font-semibold mb-1">19금 여부</label>
            <div className="flex gap-6">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={adultYn === 'Y'}
                  onChange={() => setAdultYn('Y')}
                />
                <span>19금</span>
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={adultYn === 'N'}
                  onChange={() => setAdultYn('N')}
                />
                <span>전체</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block font-semibold mb-1">서비스 지역</label>
            <div className="flex gap-6">
              {['전체', '국내', '해외'].map((s, i) => (
                <label key={i} className="flex items-center gap-1">
                  <input
                    type="radio"
                    checked={serviceType === s}
                    onChange={() => setServiceType(s)}
                  />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block font-semibold mb-1">메모</label>
            <textarea
              rows={4}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200"
            />
          </div>
        </div>

        {/* 곡별 권리사 설정 */}
        {songs.length > 0 && (
          <div className="mx-auto max-w-2xl mt-10">
            <div className="border border-white/[0.08] rounded-2xl bg-[#182334]">
              <div className="px-5 py-3 border-b border-white/[0.05]">
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest">
                  곡별 권리사 설정
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  설정하지 않으면 앨범 권리사 분배율이 적용됩니다
                </p>
              </div>
              <div className="divide-y divide-white/[0.05]">
                {songs.map((song) => {
                  const isExpanded = expandedSongs.has(song.id);
                  const hasCustom = !!songClients[song.id];
                  return (
                    <div key={song.id} className="px-5 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs text-slate-500 font-mono shrink-0">
                            {song.disc}-{String(song.track).padStart(2, '0')}
                          </span>
                          <span className="text-sm text-slate-200 font-medium truncate">
                            {song.nameKr}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {hasCustom ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">
                              커스텀
                            </span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-slate-500 font-medium">
                              기본
                            </span>
                          )}
                          {hasCustom && (
                            <button
                              onClick={() => resetSongClient(song.id)}
                              className="text-xs text-slate-500 hover:text-slate-300 px-1.5 py-1 rounded hover:bg-white/5"
                              title="앨범 기본값으로 초기화">
                              <FontAwesomeIcon icon={faRotateLeft} />
                            </button>
                          )}
                          <button
                            onClick={() => toggleSongExpand(song.id)}
                            className="text-slate-400 hover:text-white px-1.5 py-1 rounded hover:bg-white/5">
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="text-xs" />
                          </button>
                        </div>
                      </div>

                      {isExpanded && songClients[song.id] && (
                        <div className="mt-3 ml-10 space-y-2">
                          {songClients[song.id].map((sc, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <GenericAutoCompleteInput
                                value={sc}
                                onChange={(opt) => onChangeSongClient(opt, song.id, i)}
                                fetchOptions={fetchClientNames}
                                placeholder="권리사 선택"
                                index={i}
                                clients={songClients[song.id]}
                              />
                              <input
                                className="w-20 px-2 py-1.5 rounded-lg border border-white/[0.08] bg-[#1a2b42] text-slate-200 text-sm"
                                placeholder="%"
                                value={sc.feeRate ?? ''}
                                onChange={(e) => onChangeSongFeeRate(e, song.id, i)}
                              />
                              <FontAwesomeIcon
                                icon={faSquarePlus}
                                className={`text-lg ${
                                  i === songClients[song.id].length - 1
                                    ? 'text-sky-500 cursor-pointer'
                                    : 'text-gray-600'
                                }`}
                                onClick={
                                  i === songClients[song.id].length - 1
                                    ? () => addSongClient(song.id)
                                    : undefined
                                }
                              />
                              <FontAwesomeIcon
                                icon={faSquareMinus}
                                className="text-lg text-red-400 cursor-pointer"
                                onClick={() => removeSongClient(song.id, i)}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* 별도정산 관리 (관리자 전용) */}
        {isAdmin && (
          <div className="mx-auto max-w-2xl mt-10">
            <div className="border border-amber-500/30 rounded-2xl bg-[#182334]">
              <div className="px-5 py-3 border-b border-amber-500/20 flex items-center gap-2 rounded-t-2xl">
                <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-widest">별도정산</h2>
                <span className="text-xs text-slate-500">관리자 전용 · 권리사 비노출</span>
              </div>
              <div className="p-5 space-y-3">
                {/* 기존 목록 */}
                {commissionShares.length > 0 && (
                  <div className="space-y-2">
                    {commissionShares.map((cs) => (
                      <div key={cs.id} className="flex items-center gap-3">
                        <input
                          type="text"
                          value={cs.client?.name || ''}
                          readOnly
                          className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-amber-300"
                        />
                        <input
                          type="text"
                          value={`${cs.rate}%`}
                          readOnly
                          className="w-20 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-right text-amber-400 font-bold"
                        />
                        {cs.memo && (
                          <input
                            type="text"
                            value={cs.memo}
                            readOnly
                            className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-400 text-sm"
                          />
                        )}
                        <FontAwesomeIcon
                          icon={faSquareMinus}
                          className="text-2xl text-red-400 cursor-pointer shrink-0"
                          onClick={async () => {
                            if (!window.confirm('삭제하시겠습니까?')) return;
                            await axios.delete(`/api/commission-shares/${cs.id}`);
                            fetchCommissionShares();
                          }}
                        />
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-1">
                      <div className="text-xs text-slate-500">
                        합계: <span className="text-amber-400 font-bold">{commissionShares.reduce((s, c) => s + c.rate, 0)}%</span>
                      </div>
                      <button
                        disabled={retroLoading}
                        onClick={async () => {
                          if (!window.confirm('과거 정산 데이터 전체에 별도정산을 소급 적용합니다.\n계속하시겠습니까?')) return;
                          setRetroLoading(true);
                          try {
                            const res = await axios.post(`/api/commission-shares/retroactive/${id}`);
                            alert(res.data.message);
                          } catch (e) {
                            alert(e.response?.data?.error || '소급 적용 실패');
                          } finally {
                            setRetroLoading(false);
                          }
                        }}
                        className="text-xs px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 font-bold border border-amber-500/30 transition-colors disabled:opacity-50">
                        {retroLoading ? '적용 중...' : '소급 적용'}
                      </button>
                    </div>
                  </div>
                )}

                {/* 추가 폼 */}
                <div className="flex items-center gap-3 pt-2 border-t border-white/[0.06]">
                  <div className="flex-1 min-w-0">
                    <GenericAutoCompleteInput
                      value={newCs}
                      onChange={(opt) => opt && setNewCs((p) => ({ ...p, id: opt.id, name: opt.name }))}
                      fetchOptions={fetchClientNames}
                      placeholder="권리사 선택"
                    />
                  </div>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="%"
                    value={newCs.rate}
                    onChange={(e) => setNewCs((p) => ({ ...p, rate: e.target.value }))}
                    className="w-20 px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200"
                  />
                  <input
                    type="text"
                    placeholder="메모(내부용)"
                    value={newCs.memo}
                    onChange={(e) => setNewCs((p) => ({ ...p, memo: e.target.value }))}
                    className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-slate-200"
                  />
                  <FontAwesomeIcon
                    icon={faSquarePlus}
                    className="text-2xl text-amber-400 cursor-pointer shrink-0"
                    onClick={async () => {
                      if (!newCs.id || !newCs.rate) return alert('권리사와 비율을 입력하세요.');
                      await axios.post('/api/commission-shares', {
                        albumId: Number(id),
                        clientId: Number(newCs.id),
                        rate: Number(newCs.rate),
                        memo: newCs.memo,
                      });
                      setNewCs({ id: null, name: '', rate: '', memo: '' });
                      fetchCommissionShares();
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <ButtonItem
            text="목록"
            color="gray"
            onClick={() => navigate(`/Albums?page=${page}${searchParam}`)}
          />
          <ButtonItem
            text="취소"
            color="red"
            onClick={() => navigate(`/Albums/${id}?page=${page}${searchParam}`)}
          />
          <ButtonItem text="수정" color="blue" onClick={modify} />
        </div>
    </>
  );
}

export default AlbumModifyPage;
